import { type ModalCurrentRecordData } from "@softr-io/blocks-sdk";
import { useSyncExternalStore } from "react";
import { useOpenPageModal } from "./openInModal";

/**
 * Stores the record sent up from an "Open page" modal (see the effect in
 * `useCurrentRecord`). The AgentDialog panel reads it via `window.SoftrPageRenderer`.
 */
let latest: ModalCurrentRecordData | null = null;
const listeners = new Set<() => void>();

function handleModalRecordEvent(event: Event) {
    latest = (event as CustomEvent<ModalCurrentRecordData | null>).detail;
    listeners.forEach((notify) => notify());
}

/**
 * Starts listening for records sent up from "Open page" modals, and returns a
 * cleanup function.
 *
 * Call this once from the app root (`PageRoot`), not from the AgentDialog panel.
 * The panel loads lazily and might not be mounted yet when a modal sends its record
 * (e.g. a modal that's already open on page load) — if it started the listener
 * itself, that first record would be lost. Starting it at the app root means we're
 * always listening from the moment the page loads.
 */
export function setupModalRecordListener() {
    window.addEventListener("modal-current-record", handleModalRecordEvent);
    return () => window.removeEventListener("modal-current-record", handleModalRecordEvent);
}

function subscribe(notify: () => void) {
    listeners.add(notify);
    return () => listeners.delete(notify);
}

function getSnapshot() {
    return latest;
}

export function useModalRecord(): ModalCurrentRecordData | null {
    const record = useSyncExternalStore(subscribe, getSnapshot);
    return useOpenPageModal() ? record : null;
}
